import React, { useEffect, useState, useRef, createRef } from "react";
import './chat-component.scss';
import { useUserContext } from "../../userContext";
import { useSnackbar } from "../../hook/useSnackbar";
import { useZammadContext } from '../../context/ZammadContext';
import { GET_ARTICLES_BY_TICKET_ZAMMAD, CREATE_ARTICLE_ZAMMAD, fileToBase64 } from "../../utils/ZammadServices";
import { useMsal } from "@azure/msal-react";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button'

export default function ChatComponent({ infoFrm, idForm, idSolicitud, item }) {

    const { showSnackbar, SnackbarComponent } = useSnackbar();
    const { startLoader, stopLoader } = useUserContext();
    const { zammadInstance, zammadUser } = useZammadContext();
    const [historyChat, setHistoryChat] = useState([]);
    const [fileUpload, setFileUpload] = useState([]);
    const [textoChat, setTextoChat] = useState('');
    const [showChat, setShowChat] = useState(false);
    const [open, setOpen] = useState(false);
    const chatBodyRef = useRef(null);
    const { accounts } = useMsal();
    const fileInputRef = createRef();

    const handleClose = () => {
        setOpen(false);
        setFileUpload([]);
    };

    const generarFechaFutura = () => { 
        var fechaActual = new Date(); 
        fechaActual.setMonth(fechaActual.getMonth() + 1); 
        var fechaISO = fechaActual.toISOString(); 
        return fechaISO;
    }

    const scrollToBottom = () => {
        if (chatBodyRef.current) {
            const scrollHeight = chatBodyRef.current.scrollHeight;
            const height = chatBodyRef.current.clientHeight;
            const maxScrollTop = scrollHeight - height;
            chatBodyRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
        }
    }

    const formatoFechaChat = (fechaISO) => {

        // Parsear la fecha ISO
        const fecha = new Date(fechaISO);

        // Extraer componentes de la fecha
        const horas = fecha.getHours().toString().padStart(2, '0');
        const minutos = fecha.getMinutes().toString().padStart(2, '0');
        const dia = fecha.getDate().toString().padStart(2, '0');
        const mes = (fecha.getMonth() + 1).toString().padStart(2, '0'); // +1 porque getMonth() devuelve 0-11
        const año = fecha.getFullYear();

        // Construir la fecha en el nuevo formato "HH:MM DD/MM/AAAA"
       return `${horas}:${minutos} ${dia}/${mes}/${año}`;
    }


    const hasResponseAgente = (listadoHistory) => {
        // Determinar si el caso está cerrado basado en el estado
        const casoCerrado = item?.Estado === 'Finalizado';

        if (casoCerrado) {
            // Si el caso está cerrado, deshabilitar el chat
            setShowChat(true);
            return;
        }

        // Si no hay mensajes, habilitar el chat (es el primer mensaje)
        if (!listadoHistory || listadoHistory.length === 0) {
            setShowChat(false);
            return;
        }

        // Obtener el último mensaje del historial
        const ultimoMensaje = listadoHistory[listadoHistory.length - 1];

        // Deshabilitar el chat solo si el último mensaje NO es del agente
        // El chat se habilita cuando sender es "Agent" o className es "user"
        const esDelAgente = ultimoMensaje.sender === 'Agent' || ultimoMensaje.className === 'user';

        setShowChat(!esDelAgente);
    }


    const loadArticles = async () => {
        try {
            await startLoader();

            // Obtener artículos del ticket desde Zammad
            let articlesResponse = await GET_ARTICLES_BY_TICKET_ZAMMAD(zammadInstance, idSolicitud);

            let listadoHistory = articlesResponse.data
                .filter(article => article.internal === false) // Filtrar solo artículos públicos
                .map(article => {
                    return {
                        id: article.id,
                        className: article.created_by_id === zammadUser.id ? 'self' : 'user',
                        name: article.from || 'Sistema',
                        createdDate: formatoFechaChat(article.created_at),
                        text: article.body,
                        sender: article.sender || 'Customer' // Incluir el campo sender
                    };
                })
                .sort((a, b) => Number(a.id) - Number(b.id));

            setHistoryChat(listadoHistory);
            scrollToBottom();
            hasResponseAgente(listadoHistory);

            await stopLoader();
        } catch (error) {
            console.error(error);
            await stopLoader();
            showSnackbar(
                "Error : " + error.message,
                "error"
            );
        }
    };

    const handleTextoChar = (evento) => {
        setTextoChat(evento.target.value);
    };

    const sendComment = async () => {
        try {
            await startLoader();
            if (textoChat.length === 0) {
                showSnackbar(
                    "No se puede enviar contenido vacio.",
                    "error"
                );
            } else {
                await CREATE_ARTICLE_ZAMMAD(zammadInstance, {
                    ticket_id: idSolicitud,
                    subject: "Comentario del cliente",
                    body: textoChat,
                    type: "note",
                    internal: false
                });

                setTextoChat('');
                loadArticles(); // Recargar artículos
            }

            await stopLoader();
        } catch (error) {
            await stopLoader();
            showSnackbar(
                "Error al enviar comentario.",
                "error"
            );
        }
    }

    const handleFileChange = async (event) => {
        const file = event.target.files[0]; // Obten el archivo seleccionado
        if (file) {
            const newFiles = Array.from(event.target.files);
            setFileUpload(prevFiles => [...prevFiles, ...newFiles]);
            setOpen(true);
            
        } 
    };

    const handleButtonClick = () => {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
    };

    const sendUploadDocument = async () => {
        await startLoader();

        try {
            for (const file of fileUpload) {
                const base64Data = await fileToBase64(file);

                await CREATE_ARTICLE_ZAMMAD(zammadInstance, {
                    ticket_id: idSolicitud,
                    subject: "Documento adjunto",
                    body: `Documento adjunto: ${file.name}`,
                    type: "note",
                    internal: false,
                    attachments: [{
                        filename: file.name,
                        data: base64Data,
                        'mime-type': file.type || 'application/octet-stream'
                    }]
                });
            }

            setFileUpload([]);
            setOpen(false);
            loadArticles(); // Recargar artículos
        } catch (error) {
            showSnackbar("Error al subir documento.", "error");
        }

        await stopLoader();
    }

    useEffect(() => {
        if (zammadInstance && zammadUser) {
            loadArticles();
        }
        return () => {};
    }, [item, zammadInstance, zammadUser]);


    return (
        <div className='chat'>
            {SnackbarComponent}
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {" ¿ Se encuentra seguro de adjuntar el documento ?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Se enviará a busint el documento con nombre {fileUpload[0]?.name}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={sendUploadDocument} autoFocus>
                        Enviar
                    </Button>
                </DialogActions>
            </Dialog>
            <div className="chat__container">
                <div className="chat__header">
                    <div>
                        <div>
                            <img src={process.env.PUBLIC_URL + '/assets/box-img.png'} alt="" className=''/>
                        </div>
                        <div>
                            <h4>Chat</h4>
                            <h6>{infoFrm?.Title}</h6>
                        </div>
                    </div>
                    <div>
                        <button onClick={() => loadArticles()} className='phone'></button>
                    </div>
                </div>
                <div className="chat__body" ref={chatBodyRef}>
                    {
                        historyChat && historyChat.map((item , ind) => (
                            <div key={'historyid_' + ind} className={item.className}>
                                <p> {item.name} <span> {item.createdDate} </span></p>
                                <div dangerouslySetInnerHTML={{ __html: item.text }} />
                            </div>
                        ))
                    }
                </div>
                <div className="chat__footer">
                    <input type="text" value={textoChat} onChange={handleTextoChar} placeholder={showChat ? 'Se habilitará el chat, tan pronto el agente de respuesta.' : 'Escriba su mensaje..'} disabled={showChat} />
                    <div>
                        <div>
                            {/* <button className='icons'></button> */}
                            {
                                showChat ?
                                <div></div>
                                : 
                                <>
                                <input 
                                    style={{ display: 'none' }}
                                    // disabled={true}
                                    type="file" 
                                    id="image-upload-dev" 
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                />
                                <button className='file' type="file" onClick={handleButtonClick}  ></button>
                                </>
                            }
                            
                        </div>
                        <div>
                            {
                                showChat ?
                                <div></div>
                                : 
                                <button type='submit' onClick={async () => await sendComment()}>
                                    <span> Enviar mensaje </span>
                                </button>
                            }
                           
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}